import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Tone } from '../lib/format';
import { CARD, CARD_STYLE } from '../lib/ui';

const TONES: Record<Tone, { bg: string; color: string; border: string }> = {
  pink: { bg: '#fdf2f8', color: '#d6006e', border: '#fce7f3' },
  green: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
  amber: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  slate: { bg: '#f8fafc', color: '#475569', border: '#e2e8f0' },
  rose: { bg: '#fff1f2', color: '#be123c', border: '#fecdd3' },
  blue: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
};

export function StatusPill({ label, tone = 'pink', pulse }: { label: string; tone?: Tone; pulse?: boolean }) {
  const t = TONES[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${pulse ? 'mz-pulse-soft' : ''}`}
      style={{ background: t.bg, color: t.color, border: `1px solid ${t.border}` }}
    >
      {label}
    </span>
  );
}

export function ModuleHero({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mz-fade-up flex flex-wrap items-end justify-between gap-4 border-b border-[#fce7f3] pb-6">
      <div className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#d6006e]">{eyebrow}</p>
        <h1 className="mt-1.5 text-3xl font-black tracking-tight text-[#6b0030] sm:text-[2rem]">{title}</h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#b0708f]">{description}</p>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Card({ children, className = '', title, subtitle, action }: { children: ReactNode; className?: string; title?: string; subtitle?: string; action?: ReactNode }) {
  return (
    <section className={`${CARD} p-5 ${className}`} style={CARD_STYLE}>
      {(title || action) && (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            {title && <h2 className="text-base font-bold text-[#6b0030]">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-sm text-[#c084a0]">{subtitle}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export function Notice({ tone = 'green', children }: { tone?: 'green' | 'rose' | 'amber' | 'pink'; children: ReactNode }) {
  const t = TONES[tone];
  return (
    <div role={tone === 'rose' ? 'alert' : 'status'} className="rounded-xl px-3.5 py-2.5 text-sm" style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}>
      {children}
    </div>
  );
}

export function EmptyState({ title, body, cta }: { title: string; body: string; cta?: { label: string; to: string } }) {
  return (
    <div className={`${CARD} px-6 py-12 text-center`} style={CARD_STYLE}>
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#fdf2f8] text-xl text-[#d6006e]">✦</div>
      <h3 className="text-base font-bold text-[#6b0030]">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-[#c084a0]">{body}</p>
      {cta && (
        <Link to={cta.to} className="mt-4 inline-flex rounded-xl bg-[#d6006e] px-4 py-2 text-sm font-bold text-white shadow-[0_8px_24px_rgba(214,0,110,0.28)]">
          {cta.label}
        </Link>
      )}
    </div>
  );
}

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return <span className={`mz-spin inline-block rounded-full border-2 border-current border-r-transparent ${className}`} aria-hidden />;
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-10 text-sm text-[#c084a0]">
      <Spinner /> {label}
    </div>
  );
}

export function StatCard({ label, value, hint, tone = 'pink', to }: { label: string; value: ReactNode; hint?: string; tone?: Tone; to?: string }) {
  const t = TONES[tone];
  const body = (
    <>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      <p className="mt-1.5 text-3xl font-black tracking-tight" style={{ color: t.color }}>
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-[#b0708f]">{hint}</p>}
    </>
  );
  return to ? (
    <Link to={to} className={`${CARD} mz-card-link block p-4`} style={CARD_STYLE}>
      {body}
    </Link>
  ) : (
    <div className={`${CARD} p-4`} style={CARD_STYLE}>
      {body}
    </div>
  );
}

export type Person = { id?: string; name: string | null; email: string; phone?: string | null } | null | undefined;

export function PersonCard({ person, label, empty = 'Not assigned yet' }: { person: Person; label: string; empty?: string }) {
  return (
    <div className="rounded-xl px-3 py-2.5" style={{ background: '#fff5f8', border: '1px solid #fce7f3' }}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">{label}</p>
      {person ? (
        <div className="mt-1 flex items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#d6006e] text-xs font-black text-white">
            {(person.name || person.email).slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#6b0030]">{person.name || person.email}</p>
            <p className="truncate text-xs text-[#d6006e]">{person.email}</p>
            {person.phone && <p className="truncate text-xs text-[#c084a0]">{person.phone}</p>}
          </div>
        </div>
      ) : (
        <p className="mt-1 text-sm text-[#c084a0]">{empty}</p>
      )}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string; count?: number; disabled?: boolean }[];
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          disabled={o.disabled}
          onClick={() => !o.disabled && onChange(o.id)}
          title={o.disabled ? 'Available after the customer approves partner sharing' : undefined}
          className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${
            o.disabled
              ? 'cursor-not-allowed bg-[#f8fafc] text-[#94a3b8]'
              : value === o.id
                ? 'bg-[#d6006e] text-white shadow-[0_6px_16px_rgba(214,0,110,0.28)]'
                : 'bg-white text-[#6b0030] hover:bg-[#fdf2f8]'
          }`}
          style={o.disabled || value === o.id ? (o.disabled ? { border: '1px solid #e2e8f0' } : undefined) : { border: '1px solid #fce7f3' }}
        >
          {o.label}
          {o.count != null && <span className={`ml-1.5 ${value === o.id && !o.disabled ? 'text-white/80' : 'text-[#c084a0]'}`}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}
